import { CommonModule, DatePipe } from "@angular/common";
import { Component, inject, OnInit, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Store } from "@ngrx/store";
import { OrgSupplementalSystemActions } from "@store/actions/org-supplemental-system/org-supplemental-system.actions";
import { OrganizationEMRDetailsActions } from "@store/actions/organization-emr-details/organization-emr-details.actions";
import { selectSupplementalSystems } from "@store/selectors/org-supplemental-system/org-supplemental-system.selector";
import { selectOrganizationEMRForm } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { ConnectFormDirective } from "@directives/connect-form/connect-form.directive";
import { AgGridAngular } from "ag-grid-angular";
import type { ColDef, GridOptions, ValueFormatterParams } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { myCustomTheme } from "../../constants/grid-themes";
import { EditDeleteRendererComponent } from "@components/shared/edit-delete-renderer/edit-delete-renderer.component";
import { CheckboxRendererComponent } from "@components/shared/checkbox-renderer/checkbox-renderer.component";
import { Subject } from "rxjs";
import { distinctUntilChanged, takeUntil } from "rxjs/operators";
import { OrganizationSupplementalSystem } from "@models/organizationSupplementalSystem.model";
import { STANDARD_FREQUENCIES } from "@constants/frequency.constants";

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: "app-organization-ehr-form",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatRadioModule,
    MatDividerModule,
    MatIconModule,
    MatTooltipModule,
    ConnectFormDirective,
    AgGridAngular,
  ],
  providers: [DatePipe],
  templateUrl: "./organization-emr-form.component.html",
  styleUrl: "./organization-emr-form.component.css",
})
export class OrganizationEMRFormComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly fb = inject(FormBuilder);
  private readonly datePipe = inject(DatePipe);
  private readonly destroy$ = new Subject<void>();

  parentForm!: FormGroup;
  emrForm!: FormGroup;

  form$ = this.store.select(selectOrganizationEMRForm);
  systemRowData: OrganizationSupplementalSystem[] = [];

  public theme = myCustomTheme;

  public gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    domLayout: "autoHeight",
  };

  public defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
  };

  public colDefs: ColDef[] = [
    { headerName: "Name", field: "vendorName", flex: 2 },
    {
      headerName: "System",
      field: "systemType",
      flex: 1.5,
    },
    { headerName: "Version", field: "productVersion" },
    {
      headerName: "Frequency",
      field: "frequencyOfDelivery",
      valueFormatter: (params: ValueFormatterParams) => {
        const freq: string = params.value ?? "";
        return STANDARD_FREQUENCIES.includes(freq) ? freq : freq ? "Other" : "";
      },
      tooltipValueGetter: (params) => {
        const freq: string = params.value ?? "";
        if (freq && !STANDARD_FREQUENCIES.includes(freq)) {
          return `Other - ${freq}`;
        }
        return null;
      },
    },
    {
      headerName: "Type",
      field: "deliveryMethod",
      valueFormatter: (params: ValueFormatterParams) => {
        const val: string = params.value ?? "";
        return val.charAt(0).toUpperCase() + val.slice(1);
      },
    },
    { headerName: "File Format", field: "fileFormat" },
    { headerName: "External ID", field: "externalID" },
    {
      headerName: "Start Date",
      field: "effectiveDate",
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? (this.datePipe.transform(params.value, "MM/dd/yyyy") ?? "") : "",
    },
    {
      headerName: "End Date",
      field: "expirationDate",
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? (this.datePipe.transform(params.value, "MM/dd/yyyy") ?? "") : "",
    },
    {
      headerName: "Active",
      field: "statusID",
      maxWidth: 90,
      cellRenderer: CheckboxRendererComponent,
      valueGetter: (params) => params.data?.statusID === 1000,
    },
    {
      headerName: "Actions",
      field: "actions",
      filter: false,
      maxWidth: 100,
      cellRenderer: EditDeleteRendererComponent,
      cellRendererParams: { showEdit: true, showDelete: false },
    },
  ];

  ngOnInit(): void {
    this.initializeForm();

    this.store.select(selectSupplementalSystems).pipe(takeUntil(this.destroy$)).subscribe((systems) => {
      this.systemRowData = systems;
    });

    this.emrForm.get('isMemberOfACO')!.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value: string) => {
      this.store.dispatch(OrganizationEMRDetailsActions.isACOChanged({ value: Number(value) }));
    });
  }

  private initializeForm(): void {
    this.emrForm = this.fb.group({
      isMemberOfACO: new FormControl("0"),
    });

    this.parentForm = this.ctrlContainer.form;
    this.parentForm.addControl("emrInfo", this.emrForm);
  }

  onAddClicked(): void {
    this.store.dispatch(OrgSupplementalSystemActions.openAddPressed());
  }

  onCellClicked(params: any): void {
    if (
      params.column.colId === "actions" &&
      params.event.target.dataset.action === "edit"
    ) {
      this.store.dispatch(
        OrgSupplementalSystemActions.openEditPressed({ system: params.data })
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
